export class NumberUtils {
  static formatMonetaire(montant: number): string {
    if (montant === 0) return "0,00 €";

    const absValue = Math.abs(montant);

    let formatted: string;

    if (absValue >= 1) {
      // Format standard avec 2 décimales et séparateur français
      formatted = montant.toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } else if (absValue >= 0.01) {
      // Format avec 2 à 4 décimales pour petites valeurs
      formatted = montant.toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      });
    } else {
      // Très petite valeur : jusqu’à 6 décimales, pas d’arrondi brutal
      formatted = montant.toLocaleString("fr-FR", {
        minimumFractionDigits: 4,
        maximumFractionDigits: 6,
      });
    }

    return `${formatted} €`;
  }

  static formatNombreReprésentatif(montant: number): string {
    if (montant === 0) return "0";

    const absValue = Math.abs(montant);
    let formatted: string;

    if (Number.isInteger(montant)) {
      // Entier : aucun chiffre après la virgule
      formatted = montant.toLocaleString("fr-FR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    } else if (absValue >= 1) {
      // Affiche 2 décimales
      formatted = montant.toLocaleString("fr-FR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    } else if (absValue >= 0.01) {
      // Affiche 2 à 4 décimales
      formatted = montant.toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      });
    } else {
      // Très petites valeurs : jusqu’à 6 décimales
      formatted = montant.toLocaleString("fr-FR", {
        minimumFractionDigits: 4,
        maximumFractionDigits: 6,
      });
    }

    return formatted;
  }
}

/**
 * Arrondi intelligent basé sur la magnitude (même logique que le pipe numberSig).
 * Retourne un nombre, pas un string formaté.
 */
export function roundSig(n: number): number {
  if (!Number.isFinite(n) || n === 0) return n;
  const abs = Math.abs(n);
  let decimals: number;
  if (abs >= 100) decimals = 2;
  else if (abs >= 10) decimals = 3;
  else if (abs >= 1) decimals = 4;
  else {
    const exp = Math.floor(Math.log10(abs));
    decimals = Math.max(0, 4 - exp);
  }
  const factor = Math.pow(10, decimals);
  return Math.round(n * factor) / factor;
}

export function parseNumber(value): number {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    return Number(value.replace(",", "."));
  }
  return Number(value);
}

/**
 * Chiffres SIGNIFICATIFS retenus. Significatifs et non décimales : la dérive
 * flottante est *relative*, donc le remède doit l'être aussi — un seuil en
 * décimales marcherait sur des kilos et pas sur des tonnes ni des grammes.
 *
 * ⚠️ **12 et pas 15.** Un `double` porte ~15 chiffres et viser « le plus précis
 * possible » est précisément le réglage qui échoue : la dérive d'une somme croît
 * avec le nombre de termes et dépasse vite le 15e chiffre. Mesuré : `100 x 0,1`
 * rend `9.99999999999998`, que 15 chiffres NE corrigent pas et que 12 corrigent.
 * 12 laissent trois ordres de grandeur de marge tout en préservant tout écart
 * réel (sur 1776,9 kg, deux valeurs distantes de 1e-8 kg restent distinctes).
 */
const CHIFFRES_SIGNIFICATIFS = 12;

/**
 * **Somme d'une liste de quantités, ramenée à la précision métier.**
 *
 * Une addition de décimaux en binaire ne retombe pas sur le nombre décimal
 * attendu : les 8 palettes d'un déchargement réel (225,6 + 220,8 + 220,4 +
 * 223,8 + 219,5 + 227,5 + 220,3 + 219) valent 1776,9 sur le papier mais
 * `1776.8999999999999` en machine. Comparée à la quantité déchargée saisie
 * d'un bloc (`1776.9`, un nombre machine VOISIN mais distinct), la somme était
 * jugée inférieure : la ligne restait « en cours » pour 0,00000000000023 kg,
 * l'écran affichant pourtant deux fois 1776,90 et un restant à 0,00 (le pipe
 * d'affichage arrondit, la comparaison non). Constaté chez un client le
 * 04/09/2026 sur DE2600066, déchargement jamais finalisable.
 *
 * Arrondir la somme fait retomber les deux chemins de calcul sur le MÊME
 * nombre machine : `>=` redevient exact et aucune tolérance n'est nécessaire.
 *
 * ⚠️ Ne jamais remplacer par un `reduce` nu : c'est précisément ce qui a
 * produit le défaut, écrit à l'identique à trois endroits (front x2, back).
 */
export function sommeQuantites(valeurs: readonly number[]): number {
  if (!valeurs?.length) return 0;
  let total = 0;
  for (const v of valeurs) total += Number(v) || 0;
  return arrondiQuantite(total);
}

/** Ramène une quantité à la précision métier (cf. `sommeQuantites`). */
export function arrondiQuantite(n: number): number {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  if (v === 0) return 0;
  return Number(v.toPrecision(CHIFFRES_SIGNIFICATIFS));
}

/** Chiffres significatifs retenus sous 1 — en dessous, chaque chiffre pèse. */
const SIG_SOUS_UN = 5;
/** Au-delà, on est dans le bruit de virgule flottante (1.4803e-17 sur des prix identiques). */
const MAX_DECIMALES = 8;

/**
 * **Le nombre de décimales que mérite une valeur, selon sa magnitude.**
 *
 * Extraite du pipe pour qu'il n'existe qu'UNE règle : un champ de saisie ne peut pas employer le pipe
 * lui-même — `formatNumber(…, "fr-FR")` insère un **séparateur de milliers** (espace insécable étroite),
 * et `Number("1 234,5".replace(",", "."))` rend `NaN`. Une quantité au-dessus de mille redeviendrait donc
 * illisible pour le code qui la relit, alors qu'elle s'affiche normalement. Recopier la règle dans le
 * composant l'aurait fait diverger du pipe au premier ajustement, et l'écart n'aurait sauté aux yeux de
 * personne : les deux nombres restent plausibles.
 *
 * ⚠️ **Zéro rend 0** — `Math.log10(0)` vaut `-Infinity` et rendrait `Infinity` décimales. Le pipe traite
 * le zéro avant d'arriver ici ; un appelant qui l'oublierait obtiendrait un nombre, pas une exception.
 */
export function decimalesSelonMagnitude(
  n: number,
  opts: { defaultSig?: number; maxDecimals?: number } = {},
): number {
  if (!Number.isFinite(n) || n === 0) return 0;

  const abs = Math.abs(n);
  let decimals: number;

  if (abs >= 100) {
    decimals = 2;
  } else if (abs >= 10) {
    decimals = 3;
  } else if (abs >= 1) {
    decimals = 4;
  } else {
    // abs < 1 : utiliser 5 chiffres significatifs
    const sig = opts.defaultSig ?? SIG_SOUS_UN;
    const exp = Math.floor(Math.log10(abs));
    decimals = Math.max(0, sig - 1 - exp);
  }

  // Clamp : au-delà de ce nombre de décimales, on est dans le bruit float.
  return Math.min(decimals, opts.maxDecimals ?? MAX_DECIMALES);
}
